<template>
    <div v-if="isQueueBreak(fight)" class="single-fight queue-break">
        <div class="top-header single-fight-content-row">
            <div class="is-flex-grow-1">
                <span class="icon">
                    <i class="fa fa-coffee" />
                </span>

                <span class="has-text-weight-bold mr-1" v-if="startTime">
                    {{ fightStartInEventTimezone(startTime) }}
                </span>

                <span>
                    {{ fight.category }}
                </span>

                <span class="tag" v-if="liveFightTime">
                    <span class="icon"> 🏁 </span>
                    <span>
                        {{ liveFightTime }}
                    </span>
                </span>
                <span class="tag" v-if="liveFightRoundStatus">
                    {{ liveFightRoundStatus }}
                </span>
            </div>

            <div v-if="matName" class="is-text-wrap-nowrap">
                <span class="icon">
                    <i class="fa fa-map-marker-alt" />
                </span>
                <span>{{ matName }}</span>
            </div>
        </div>
    </div>
    <div v-else class="single-fight">
        <div v-if="showCategoryHeader" class="top-header single-fight-content-row" :class="getFightStatusClass()">
            <div class="is-flex-grow-1">
                <span class="mr-1">
                    <FightNumber :fight="fight" :fight-index="fightIndex" />
                </span>

                <span class="has-text-weight-bold mr-1" v-if="startTime">
                    {{ fightStartInEventTimezone(startTime) }}
                </span>

                <a v-if="!disableCategoryLink" class="mr-1" @click="openBracketFightsOverviewSidebar(fight.parameterId)">
                    {{ fight.category }}
                </a>
                <span v-else class="mr-1">{{ fight.category }}</span>

                <BracketTypeBadge class="mr-1" :fight="fight" />

                <FightRoundName class="mr-1" :round-name="fight.roundName" :bracket-level="fight.bracketLevel" />

                <span class="tag" v-if="liveFightTime">
                    <span class="icon"> 🏁 </span>
                    <span>
                        {{ liveFightTime }}
                    </span>
                </span>
                <span class="tag" v-if="liveFightRoundStatus">
                    {{ liveFightRoundStatus }}
                </span>
            </div>

            <div v-if="matName" class="is-text-wrap-nowrap">
                <span class="icon">
                    <i class="fa fa-map-marker-alt" />
                </span>
                <span>{{ matName }}</span>
            </div>
        </div>

        <slot name="top-slot" :fight="fight"></slot>

        <SingleCompetitor
            :competitor="fight.firstCompetitor"
            class="single-fight-content-row"
            :class="getWinnerOrLoserClass(fight.firstCompetitor.id)"
            :is-winner="isWinner(fight.firstCompetitor.id)"
            :selectable="!!onCompetitorClick"
            :show-branch="showBranch"
            :show-avatar="showAvatar"
            competitor-side="first"
            :color-schema="colorSchema"
            @selected="onCompetitorClick"
        >
            <template v-slot:after-name>
                <span v-if="liveFightPoints" class="tag mr-2 has-text-light-bold">
                    {{ liveFightPoints.firstCompetitorPoints }}
                </span>
                <slot name="after-first-competitor" :competitor="fight.firstCompetitor" :fight="fight" />
            </template>
        </SingleCompetitor>

        <SingleCompetitor
            :competitor="fight.secondCompetitor"
            class="single-fight-content-row"
            :class="getWinnerOrLoserClass(fight.secondCompetitor.id)"
            :is-winner="isWinner(fight.secondCompetitor.id)"
            :selectable="!!onCompetitorClick"
            :show-branch="showBranch"
            :show-avatar="showAvatar"
            competitor-side="second"
            :color-schema="colorSchema"
            @selected="onCompetitorClick"
        >
            <template v-slot:after-name>
                <span v-if="liveFightPoints" class="tag mr-2 has-text-light-bold">
                    {{ liveFightPoints.secondCompetitorPoints }}
                </span>
                <slot name="after-second-competitor" :competitor="fight.secondCompetitor" :fight="fight" />
            </template>
        </SingleCompetitor>
    </div>
</template>

<script setup lang="ts">
    import { computed } from "vue";
    import FightRoundName from "../FightRoundName.vue";
    import BracketTypeBadge from "../BracketTypeBadge.vue";
    import { STATUS_AWAITING_COMPETITORS, STATUS_FIGHT_IN_PROGRESS } from "../consts";
    import timeFormatter from "../timeFormatter";
    import { convertToTimezone } from "../dateTimeUtils";
    import SingleCompetitor from "./SingleCompetitor.vue";
    import FightNumber from "./FightNumber.vue";
    import { FightQueueStatus, isQueueBreak } from "./types";
    import { RegisteredCompetitorWithoutParameterId } from "../Competitors/types";
    import { BracketType, ColorSchema } from "../types";
    import { OPEN_BRACKET_FIGHTS_OVER_VIEW_SIDEBAR, singleFightEventBus } from "./singleFightEvents";
    import { formatLiveFightRoundStatus } from "../../Public/liveFightScores";
    import type { LiveFightRoundStatus, LiveFightScore } from "../../Public/liveFightScores";
    import { translate } from "../translations";

    type FightType = {
        id: number;
        firstCompetitor: RegisteredCompetitorWithoutParameterId;
        secondCompetitor: RegisteredCompetitorWithoutParameterId;
        winnerCompetitor: RegisteredCompetitorWithoutParameterId | null;
        fightNumber: number | null;
        category: string;
        parameterId: number;
        matId: number;
        roundName: string;
        bracketLevel: number;
        bracketType: BracketType;
    };

    const props = defineProps<{
        fight: FightType;
        startTime: string | null;
        fightIndex: number;
        eventTimezone: string | null;
        fightQueueStatuses: Record<number, FightQueueStatus>;
        trackedTimeForFights: Record<number, number>;
        trackedPointsForFights?: Record<number, LiveFightScore>;
        trackedRoundStatusesForFights?: Record<number, LiveFightRoundStatus>;
        showCategoryHeader?: boolean;
        showWinnerAndLoser?: boolean;
        showBranch?: boolean;
        showAvatar?: boolean;
        onCompetitorClick?: (competitorId: number) => void;
        colorSchema?: ColorSchema;
        disableCategoryLink?: boolean;
        matName?: string;
    }>();

    function isWinner(competitorId: number) {
        if (props.fight.winnerCompetitor === null) {
            return false;
        }

        return competitorId === props.fight.winnerCompetitor.id;
    }

    function getWinnerOrLoserClass(competitorId: number) {
        if (!props.showWinnerAndLoser) {
            return "";
        }

        if (props.fight.winnerCompetitor === null) {
            return "";
        }

        if (competitorId !== props.fight.winnerCompetitor.id) {
            return "is-loser";
        }

        return "";
    }

    function fightStartInEventTimezone(startTime: string) {
        if (!props.eventTimezone) {
            return "";
        }
        return convertToTimezone(startTime, props.eventTimezone, "HH:mm");
    }

    function getFightStatusClass() {
        const statusForMat = props.fightQueueStatuses[props.fight.matId];

        if (statusForMat?.fightId !== props.fight.id) {
            return "";
        }

        if (statusForMat.status === STATUS_AWAITING_COMPETITORS) {
            return "fight-active fight-awaiting-competitor";
        }
        if (statusForMat.status === STATUS_FIGHT_IN_PROGRESS) {
            return "fight-active fight-in-progress";
        }

        return "";
    }

    // Tracking time
    const liveFightTime = computed(() => {
        const secondsLeft = props.trackedTimeForFights[props.fight.id];

        if (secondsLeft === undefined) {
            return "";
        }

        //Return pretty time
        return timeFormatter.formatSecondsToMinutesAndSeconds(secondsLeft);
    });

    const liveFightPoints = computed(() => {
        return props.trackedPointsForFights?.[props.fight.id] ?? null;
    });

    const liveFightRoundStatus = computed(() => {
        if (!liveFightTime.value) {
            return "";
        }

        const roundStatus = props.trackedRoundStatusesForFights?.[props.fight.id];

        if (!roundStatus) {
            return "";
        }

        return formatLiveFightRoundStatus(roundStatus, {
            breakBetweenRounds: translate("scoreboard.break_between_rounds"),
            overtime: translate("scoreboard.overtime"),
            goldenScore: translate("scoreboard.golden_score"),
        });
    });

    // Sidebar

    function openBracketFightsOverviewSidebar(parameterId: number) {
        singleFightEventBus.$emit(OPEN_BRACKET_FIGHTS_OVER_VIEW_SIDEBAR, parameterId);
    }
</script>

<style scoped></style>
